/**
 * Riders Corner Mapper Utilities
 *
 * Utility functions for mapping and normalizing riders corner data
 * Follows Single Responsibility Principle: Each function has one clear purpose
 */

import { RidersCornerListItem } from '@/services/api/interfaces/IRidersCornerRepository';

/**
 * Category can be either a string or an object with title and other properties
 */
export type CategoryInput =
  | string
  | {
      uid?: string;
      title?: string;
      description?: string;
      icon?: string;
      imageUrl?: string;
      videoUrl?: string;
      snapVideoUrl?: string;
      _id?: string;
    }
  | null
  | undefined;

/**
 * Normalizes a category input (string or object) into the standard category object format
 *
 * @param category - Category as string or object
 * @param defaultValue - Default title if category is missing (default: 'Ride')
 * @returns Normalized category object
 */
export function normalizeCategory(
  category: CategoryInput,
  defaultValue: string = 'Ride'
): RidersCornerListItem['category'] {
  // Handle null/undefined
  if (!category) {
    return {
      uid: '',
      title: defaultValue,
      description: '',
      icon: '',
      imageUrl: '',
      videoUrl: '',
      snapVideoUrl: '',
      _id: '',
    };
  }

  // Handle string category
  if (typeof category === 'string') {
    return {
      uid: category,
      title: category,
      description: '',
      icon: '',
      imageUrl: '',
      videoUrl: '',
      snapVideoUrl: '',
      _id: '',
    };
  }

  // Handle object category
  return {
    uid: category.uid || '',
    title: category.title || defaultValue,
    description: category.description || '',
    icon: category.icon || '',
    imageUrl: category.imageUrl || '',
    videoUrl: category.videoUrl || '',
    snapVideoUrl: category.snapVideoUrl || '',
    _id: category._id || '',
  };
}

/**
 * Raw API response item type (before mapping)
 */
export interface RawRidersCornerApiItem {
  _id?: string;
  title?: string;
  shortTitle?: string;
  slug?: string;
  category?: CategoryInput;
  imageUrl?: string | string[];
  totalDistance?: number;
  totalHoursFrom?: number;
  totalHoursTo?: number;
  description?: string;
  shortDescription?: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

/**
 * Maps a raw API response item to RidersCornerListItem format
 * Handles category normalization and other field mappings
 *
 * @param item - Raw item from API response
 * @returns Mapped RidersCornerListItem
 */
export function mapApiItemToRidersCornerListItem(
  item: RawRidersCornerApiItem
): RidersCornerListItem {
  return {
    _id: item._id || '',
    title: item.title || item.shortTitle || '',
    shortTitle: item.shortTitle || item.title || '',
    slug: item.slug || item._id,
    category: normalizeCategory(item.category),
    imageUrl: Array.isArray(item.imageUrl)
      ? item.imageUrl
      : item.imageUrl
        ? [item.imageUrl]
        : [],
    totalDistance: item.totalDistance || 0,
    totalHoursFrom: item.totalHoursFrom || 0,
    totalHoursTo: item.totalHoursTo || 0,
    description: item.description || '',
    shortDescription: item.shortDescription || item.description || '',
    isActive: item.isActive !== false,
    isDeleted: item.isDeleted === true,
  };
}
